import React from 'react';
import {useTranslation} from 'react-i18next';

import {useModal} from '@hooks';
import {Building, getAllBuildings, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling} from '@lib/store';

import {Icon} from '@atoms';
import {ErrorHandlingResult} from '@lib/infrastructure';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {LocalDateString, Workday, WriteableWorkdayDetails} from '@lib/store/src/data/timeslots/apiTypes';
import {
  getScheduleForDateIsLoading,
  getScheduleForDateIsUpdating,
  getWorkdayBuilding,
} from '@lib/store/src/data/timeslots/selectors/index';
import {FlexCol, Subtitle} from '@quarks';
import {cn} from '@utils';
import {IconNames} from 'mapiq-atoms';
import {ComponentProps, PropsWithChildren} from 'react';
import {IconStatusMapping} from 'src/components/organisms/calendar/CalendarWeekDay/components/utils';
import {ModalPage} from 'src/components/organisms/ModalPage';
import {TileItem} from '../../TileItem';
import {ReservationTimeLabel} from '../WorkspaceTile/WorkspaceTile';
import {useEditWorkday} from 'src/components/organisms/calendar/CalendarWeekDay/components/QuickOfficeDayButton';

export const WorkdayTile = ({
  date: dateKey,
  workday,
  ...props
}: ComponentProps<typeof TileItem> & {date: string; workday: Workday | null}) => {
  const editWorkday = useEditWorkday().openModal;

  const loadingExistingData = useAppSelector(getScheduleForDateIsLoading(dateKey));
  const mutating = useAppSelector(getScheduleForDateIsUpdating(dateKey));
  const building = useAppSelector((state) => getWorkdayBuilding(state, workday));

  const loading = loadingExistingData || mutating;

  return (
    <TileItem
      data-testid="workday-tile"
      className="bg-energizing-yellow-500*" // is always highlighted
      disabled={mutating}
      onClick={() => {
        editWorkday(workday, dateKey);
      }}
      {...props}>
      <Icon
        className="size-5"
        icon={resolveWorkdayStatusIcon(loading, workday)}
      />

      <h4 className="flex justify-between">
        <Heading
          loading={loadingExistingData}
          updating={mutating}
          workday={workday}
          building={building}
        />

        {workday && (
          <em>
            <ReservationTimeLabel
              start={workday.localStart}
              end={workday.localEnd}
            />
          </em>
        )}
      </h4>

      <Icon
        className="size-5 justify-self-end"
        icon={'caretRight'}
      />
    </TileItem>
  );
};

export const TemporaryWorkdayModal = ({workday, date: dateKey}: {workday: Workday | null; date: LocalDateString}) => {
  const {closeModal} = useModal();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const buildings = useAppSelector((state) => {
    const all = getAllBuildings(state);
    return all;
  });

  // TODO: use toaster
  const showToasterIfFailed = (r: ErrorHandlingResult<unknown>) => {
    if (!r.success) alert(r.result.message);
  };
  const cancelWorkday = (workday: Workday) => {
    dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkday(workday))).then(showToasterIfFailed);
  };

  const editWorkday = (workday: Workday, location: WriteableWorkdayDetails) => {
    dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.updateWorkday({
          id: workday.id,
          localStart: workday.localStart,
          localEnd: workday.localEnd,
          ...location,
        }),
      ),
    ).then(showToasterIfFailed);
  };

  const createWorkday = (location: WriteableWorkdayDetails) => {
    dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          ...location,
          // TODO: set start/end times
          localStart: `${dateKey}T00:00`,
          localEnd: `${dateKey}T23:59`,
        }),
      ),
    ).then(showToasterIfFailed);
  };

  const handleChange = (location: WriteableWorkdayDetails | null) => {
    // Don't await these actions; assume they succeed
    if (workday && !location) cancelWorkday(workday);
    else if (workday && location) editWorkday(workday, location);
    else if (location) createWorkday(location);
    else console.warn('Unsupported change');

    closeModal();
  };

  return (
    <ModalPage
      onClose={closeModal}
      title={t('workplace:YourLocation')}>
      <FlexCol gap={32}>
        <Label>
          <input
            type="radio"
            defaultChecked={!workday}
            onChange={(e) => {
              if (!e.target.checked) return;

              handleChange(null);
            }}
          />
          No plans registered
        </Label>
        <hr />
        <Label>
          <input
            type="radio"
            defaultChecked={workday?.status === 'NotWorking'}
            onChange={(e) => {
              if (!e.target.checked) return;

              handleChange({
                status: 'NotWorking',
              });
            }}
          />
          Not working
        </Label>
        <Label>
          <input
            type="radio"
            defaultChecked={workday?.status === 'WorkingRemotely'}
            onChange={(e) => {
              if (!e.target.checked) return;

              handleChange({
                status: 'WorkingRemotely',
              });
            }}
          />
          Working remotely
        </Label>

        <Label>
          <input
            type="radio"
            readOnly
            disabled
            defaultChecked={workday?.status === 'OfficeDay'}
          />
          Working at the office
        </Label>
        {buildings.map((b) => (
          <Label
            key={b.id}
            indent={true}>
            <input
              type="radio"
              defaultChecked={workday?.status === 'OfficeDay' && workday.buildingId === b.id}
              onChange={(e) => {
                if (!e.target.checked) return;

                handleChange({
                  status: 'OfficeDay',
                  buildingId: b.id,
                });
              }}
            />
            <span className="flex gap-2">
              <span>{b.name}</span>
              {!b.allowWorkdayWithoutWorkspace && (
                <span
                  className="opacity-40"
                  title="workspace booking required">
                  ⚠️
                </span>
              )}
            </span>
          </Label>
        ))}

        <Subtitle fontSize="sm">{t('workplace:NoOfficeInTheList')}</Subtitle>
      </FlexCol>
    </ModalPage>
  );
};

const Label = ({children, indent = false}: PropsWithChildren<{indent?: boolean}>) => {
  return <label className={cn('flex gap-2 items-center', {'ml-4': indent})}>{children}</label>;
};

const Heading = ({
  loading,
  updating,
  workday,
  building,
}: {
  loading: boolean;
  updating: boolean;
  workday: Workday | null;
  building: Building | null;
}) => {
  const {t} = useTranslation();

  if (loading) return null;
  if (updating) return t('workplace:UpdatingWorkday');
  if (!workday) return t('workplace:WhereWillYouBe');
  if (building) return building.name;
  if (workday.status === 'NotWorking') return t('workplace:NotWorking');
  if (workday.status === 'WorkingRemotely') return t('workplace:WorkingRemotely');

  if (workday.status === 'OfficeDay' && !building) {
    console.warn('Warning: workday in building that is unavailable');
    return workday.buildingName;
  }

  // TODO: Unknown status??
  return `[[ UNKNOWN ]]`;
};

const resolveWorkdayStatusIcon = (loading: boolean, workday: Workday | null): IconNames => {
  if (loading) return 'dots';
  if (!workday) return 'locationUnknown';

  // TODO: Unknown status??
  return IconStatusMapping[workday.status] ?? 'locationUnknown';
};
